// Copyright (c) 2026, Cyber Cube and contributors
// For license information, please see license.txt
//
// The **client mirror** of the compliance rule engine — `PHASE-9.md` §8.5's
// *"live rule feedback: evaluated as the value changes and repeated server-side
// on save; the client never decides the stored verdict."*
//
// ## What this is, and what it is not
//
// It is a **display**. `qhse/utils/facility_compliance.py::evaluate_answer` is the
// engine; `save_responses` re-runs it on every save and the stored `verdict` /
// `auto_verdict` are always its answer (ARCH §6.20). Nothing here is written
// anywhere. If this file and the server ever disagree, the server is right and
// this file is a bug — and the shipped test suite is what turns that from a hope
// into a failing check:
// `qhse/utils/facility_rule_cases.json` is one fixture table read by
// **both** sides, and
// `qhse/qhse/page/test_facility_page.py::TestTheClientMirrorAgreesWithTheEngine`
// runs every case through this file in node and through the Python engine, and
// fails on the first divergence.
//
// ## Why a second implementation exists at all
//
// §8.5's contract is live feedback *as the value changes*. A round trip per
// keystroke is not that: it is one request per digit of a pressure reading, on a
// phone, on a site with no signal. The milestone declares this as accepted debt
// (`Resolve by: none-planned`) with one hard boundary — **if the mirror is ever
// allowed to decide a stored value, that is a milestone failure, not debt.**
//
// ## The rules that are easy to get subtly wrong, and are therefore spelled out
//
//   * `Between` is **inclusive at both ends** and `Outside` is its exact
//     complement. An off-by-one here mis-decides one reading in a hundred and is
//     invisible on screen.
//   * Text is **always Pending** — blank because it was not answered, written
//     because no rule can judge prose. There is no input that makes a text
//     question Compliant, and M9.19's acknowledgement is not an exception: it
//     changes the reason and it excuses a **mandatory** text question from
//     holding its entry at `Pending` (`mandatoryOutstanding`), it never grades.
//   * An acknowledgement counts only while the answer box is non-empty. Without
//     that clause "mark it answered" is "mark it skipped", which is the thing
//     the owner chose this repair over.
//   * `answer_check` blank is `null`, never falsiness: `0` is a real answer, and
//     reading an unticked box as "unanswered" would hide every negative finding.
//   * A Select value in neither list is **Pending**, with the value quoted and the
//     options listed (ARCH §10.13's silent Select failure, made loud).
//   * Photo / Document with no file is **Pending, never Non-Compliant**: a missing
//     photograph is missing evidence, not a failed asset.
//   * `not_applicable` is honoured only when the question sets `allow_na`.
//
// The reason strings are the server's, word for word, because they are part of the
// contract (§6.18) and because a reason that changes on save would tell the
// inspector the screen lied to them.

frappe.provide("qhse.facility");

const DEFAULT_PRECISION = 2;
const MAX_PRECISION = 6;

const TEXT_TYPES = ["Short Text", "Long Text"];
const ATTACHMENT_TYPES = ["Photo", "Document"];
const RANGE_RULES = ["Between", "Outside"];
const COMPLIANT_WHEN = ["Checked", "Unchecked"];

// M9.19. Mirrors `facility_compliance.GRADED_VERDICTS` and
// `TEXT_ACKNOWLEDGED_FIELD`. `GRADED` is `RESPONSE_VERDICTS` minus `Pending`:
// anything outside it leaves a mandatory response outstanding, which is the
// fail-closed direction and the reading the server takes.
const GRADED_VERDICTS = ["Compliant", "Non-Compliant", "Not Applicable"];
const TEXT_ACKNOWLEDGED_FIELD = "text_acknowledged";

// Mirrors `facility_compliance.QUESTION_DEFAULTS` for the four fields this file
// falls back on. Not the whole map: a default this file does not read is a
// default this file must not restate (one copy per consumer is already one too
// many, and an unread copy is a copy nothing would notice going stale).
const DEFAULTS = {
	answer_type: "Short Text",
	numeric_rule: "No Rule",
	compliant_when: "Checked",
	date_rule: "No Rule",
};

// `_get` / `_text` / `_flag` / `_number` / `_int` / `_lines` — the Python
// coercions, one for one. Every one of them returns a value and none of them
// throws on user data: the engine must not be able to break a capture screen an
// inspector is standing in front of.

function get(source, key, fallback) {
	if (source === null || source === undefined) return fallback;
	const value = source[key];
	return value === null || value === undefined ? fallback : value;
}

function text(source, key) {
	const value = get(source, key, "");
	return value === null || value === undefined ? "" : String(value).trim();
}

function flag(source, key) {
	const value = get(source, key, 0);
	if (typeof value === "string") {
		const trimmed = value.trim();
		if (!trimmed) return 0;
		const parsed = Number(trimmed);
		return Number.isFinite(parsed) && parsed !== 0 ? 1 : 0;
	}
	return value ? 1 : 0;
}

function number(value) {
	if (value === null || value === undefined) return null;
	if (typeof value === "boolean") return value ? 1 : 0;
	if (typeof value === "number") return Number.isFinite(value) ? value : null;
	const trimmed = String(value).trim();
	if (!trimmed) return null;
	// `Number("")` is 0 and `Number("12abc")` is NaN — the blank is handled above
	// so only the NaN arm remains, which is the Python `float()` ValueError.
	//
	// **One deliberate narrowing, stated rather than discovered.** Python's
	// `float()` accepts `"inf"`, `"-infinity"` and `"nan"`; `Number()` accepts only
	// `"Infinity"`, and `Number.isFinite` rejects all of them here. So a literal
	// `"nan"` sitting in an `answer_number` column reads as *"not a number"* on the
	// client and as a non-finite float on the server. The divergence is in the safe
	// direction — this side degrades to **Pending**, never to Compliant — and the
	// input is unreachable from the capture screen, whose control is
	// `<input type="number">`. Recorded in PROGRESS rather than left to be found.
	const parsed = Number(trimmed);
	return Number.isFinite(parsed) ? parsed : null;
}

function integer(value, fallback) {
	const parsed = number(value);
	return parsed === null ? fallback : Math.trunc(parsed);
}

function lines(value) {
	if (!value) return [];
	return String(value)
		.split("\n")
		.map((line) => line.trim())
		.filter((line) => line.length > 0);
}

/** `YYYY-MM-DD` as a UTC-noon Date, or null.
 *
 * **Noon, not midnight, and that is not cosmetic.** A date built at 00:00 local
 * time in a negative-UTC-offset zone lands on the previous day once it is read
 * back as UTC, so `(value - today).days` comes out one short and "expires in 44
 * days" renders as 43. Both sides of every subtraction here are built the same
 * way and compared in UTC, so the day count is the calendar difference the server
 * computes with `datetime.date` arithmetic.
 */
function asDate(value) {
	if (value === null || value === undefined) return null;
	if (value instanceof Date) {
		return Number.isNaN(value.getTime())
			? null
			: new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate(), 12));
	}
	const raw = String(value).trim();
	if (!raw) return null;
	const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
	if (!match) return null;
	const [, year, month, day] = match;
	const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), 12));
	if (Number.isNaN(date.getTime())) return null;
	// A rolled-over date (`2026-02-31`) is not a date this engine can read, which
	// is what the Python `strptime` arm decides too.
	if (date.getUTCMonth() !== Number(month) - 1 || date.getUTCDate() !== Number(day)) {
		return null;
	}
	return date;
}

function isoDate(date) {
	return date.toISOString().slice(0, 10);
}

function dayDelta(value, reference) {
	return Math.round((value.getTime() - reference.getTime()) / 86400000);
}

/** `facility_compliance.format_number` — trailing zeros kept, precision clamped.
 *
 * **`toFixed` alone is not this function**, and the difference is a real one that
 * the parity fixture caught: Python's `f"{x:.2f}"` rounds the double's *exact*
 * binary value **half to even**, while `Number.prototype.toFixed` rounds the same
 * exact value with ties going **away from zero**. So `0.125` at 2 dp is `0.12` on
 * the server and `0.13` in the browser, and a reading of `14.25` at 1 dp is `14.2`
 * and `14.3`. The verdict agrees in both cases; the *sentence under the input*
 * does not, and it is the sentence the inspector believes (§6.18's reason-string
 * contract, and the same reasoning as `_shared_precision`).
 *
 * `Intl.NumberFormat`'s `roundingMode: "halfEven"` is **not** the fix: V8 rounds
 * the shortest decimal representation rather than the exact value, so it returns
 * `2.68` for `2.675` where both Python and `toFixed` return `2.67`. Verified on
 * this bench rather than assumed.
 *
 * So: `toFixed` does all the carrying (it is exact-value-based and correct for
 * every non-tie), and the only case handled by hand is an **exact tie whose kept
 * last digit is even** — where the answer is simply the truncation, which needs no
 * carry at all. The tie is detected from the double's exact decimal expansion
 * (`toFixed(100)`), not from arithmetic that would introduce the error it is
 * looking for.
 */
function formatNumber(value, precision) {
	const parsed = number(value);
	if (parsed === null) return "";
	let digits = integer(precision, DEFAULT_PRECISION);
	if (digits < 0) digits = 0;
	if (digits > MAX_PRECISION) digits = MAX_PRECISION;

	const negative = parsed < 0 || Object.is(parsed, -0);
	const magnitude = Math.abs(parsed);
	// 100 decimals is the whole exact expansion for every magnitude a reading can
	// hold: a double `m · 2^e` has exactly `|e|` decimals, and `|e| < 100` for
	// anything above ~1e-15. Above 1e21 `toFixed` switches to exponential form and
	// such a value has no fractional part to tie on, so the plain arm is exact.
	const exact = magnitude < 1e21 ? magnitude.toFixed(100) : "";
	if (exact && exact.indexOf("e") === -1) {
		const dot = exact.indexOf(".");
		const whole = exact.slice(0, dot);
		const fraction = exact.slice(dot + 1);
		const kept = fraction.slice(0, digits);
		const dropped = fraction.slice(digits);
		const isTie = /^50*$/.test(dropped);
		if (isTie) {
			const lastKept = digits === 0 ? whole.slice(-1) : kept.slice(-1);
			if (Number(lastKept) % 2 === 0) {
				// Ties to even means "stay" — the truncation, sign restored by hand
				// because `toFixed` is not involved on this path.
				const body = digits === 0 ? whole : `${whole}.${kept}`;
				return negative ? `-${body}` : body;
			}
		}
	}

	const rendered = parsed.toFixed(digits);
	// `(-0).toFixed(2)` is `"0.00"`; `f"{-0.0:.2f}"` is `"-0.00"`.
	return negative && rendered.charAt(0) !== "-" ? `-${rendered}` : rendered;
}

/** `facility_compliance._shared_precision`.
 *
 * The smallest precision at which the reading still reads differently from the
 * numbers it is compared against. Without it, a reading just below a bound renders
 * as the bound itself and the sentence contradicts the verdict — *"15.00 bar is
 * outside 15.00–20.00 bar"*. The verdict is right and the sentence is not, which
 * is the worst shape a compliance message can take because the inspector believes
 * the sentence.
 */
function sharedPrecision(value, others, precision) {
	let digits = integer(precision, DEFAULT_PRECISION);
	if (digits < 0) digits = 0;
	const parsed = number(value);
	if (parsed === null) return digits;

	const comparators = others
		.map((other) => number(other))
		.filter((other) => other !== null && other !== parsed);

	while (digits < MAX_PRECISION) {
		const rendered = formatNumber(parsed, digits);
		if (comparators.every((other) => formatNumber(other, digits) !== rendered)) {
			return digits;
		}
		digits += 1;
	}
	return digits;
}

/** The unit as it appears *after* a number, or nothing at all. */
function unitOf(uom) {
	const trimmed = (uom || "").trim();
	return trimmed ? ` ${trimmed}` : "";
}

function verdict(value, reason) {
	return { verdict: value, reason };
}

// --------------------------------------------------------------- the evaluators

function evaluateText(answer) {
	if (!text(answer, "answer_text")) {
		if (flag(answer, TEXT_ACKNOWLEDGED_FIELD)) {
			return verdict("Pending", "marked answered, but the answer box is empty");
		}
		return verdict("Pending", "not answered");
	}
	if (flag(answer, TEXT_ACKNOWLEDGED_FIELD)) {
		return verdict("Pending", "answered — marked complete by the inspector");
	}
	return verdict("Pending", "judged by the inspector");
}

/** The mirror of `facility_compliance.text_acknowledged`. */
function textAcknowledged(row) {
	if (TEXT_TYPES.indexOf(text(row, "answer_type")) === -1) return false;
	if (!flag(row, TEXT_ACKNOWLEDGED_FIELD)) return false;
	return !!text(row, "answer_text");
}

function evaluateNumeric(rule, answer) {
	const precision = integer(get(rule, "numeric_precision", DEFAULT_PRECISION), DEFAULT_PRECISION);
	const unit = unitOf(text(rule, "uom"));

	const raw = get(answer, "answer_number", null);
	const value = number(raw);
	if (value === null) {
		if (raw !== null && raw !== undefined && raw !== "") {
			return verdict("Pending", `'${raw}' is not a number`);
		}
		return verdict("Pending", "not answered");
	}

	const numericRule = text(rule, "numeric_rule") || DEFAULTS.numeric_rule;
	const low = number(get(rule, "min_value", null));
	const high = number(get(rule, "max_value", null));
	const threshold = number(get(rule, "threshold_value", null));

	const digits = sharedPrecision(value, [low, high, threshold], precision);
	const shown = formatNumber(value, digits) + unit;

	if (numericRule === "No Rule") {
		return verdict("Pending", `${shown} recorded; this question has no rule to decide it`);
	}

	if (RANGE_RULES.indexOf(numericRule) !== -1) {
		if (low === null || high === null) {
			return verdict(
				"Pending",
				`${shown} recorded; this question's ${numericRule} rule is incomplete`
			);
		}
		const lowText = formatNumber(low, digits);
		const highText = formatNumber(high, digits);
		// Inclusive at BOTH ends, and `Outside` is the exact complement.
		const within = low <= value && value <= high;
		if (numericRule === "Between") {
			if (within) {
				return verdict("Compliant", `${shown} is within ${lowText}–${highText}${unit}`);
			}
			return verdict("Non-Compliant", `${shown} is outside ${lowText}–${highText}${unit}`);
		}
		if (within) {
			return verdict(
				"Non-Compliant",
				`${shown} is within ${lowText}–${highText}${unit}, and this question requires a reading outside it`
			);
		}
		return verdict(
			"Compliant",
			`${shown} is outside ${lowText}–${highText}${unit}, as this question requires`
		);
	}

	if (threshold === null) {
		return verdict(
			"Pending",
			`${shown} recorded; this question's ${numericRule} rule has no threshold`
		);
	}

	const thresholdText = formatNumber(threshold, digits) + unit;
	const comparisons = {
		"Greater Than": [value > threshold, "greater than"],
		"Greater Than Or Equal": [value >= threshold, "at least"],
		"Less Than": [value < threshold, "less than"],
		"Less Than Or Equal": [value <= threshold, "at most"],
		"Equal To": [value === threshold, "exactly"],
	};
	const comparison = comparisons[numericRule];
	if (!comparison) {
		// The Python `comparisons[numeric_rule]` would KeyError here. A capture
		// screen must not throw on a template it does not recognise, so the
		// unknown rule is Pending and says which rule it was.
		return verdict(
			"Pending",
			`'${numericRule}' is not a numeric rule this engine can evaluate`
		);
	}
	const [passed, phrase] = comparison;
	if (passed) {
		return verdict("Compliant", `${shown} is ${phrase} ${thresholdText}, as this question requires`);
	}
	return verdict("Non-Compliant", `${shown} is not ${phrase} ${thresholdText}`);
}

function evaluateCheckbox(rule, answer) {
	const raw = get(answer, "answer_check", null);
	// `0` is a real answer here, so the blank test is `== null`, never falsiness.
	if (raw === null || raw === undefined || (typeof raw === "string" && !raw.trim())) {
		return verdict("Pending", "not answered");
	}

	const ticked = flag(answer, "answer_check");
	const wanted = text(rule, "compliant_when") || DEFAULTS.compliant_when;
	if (COMPLIANT_WHEN.indexOf(wanted) === -1) {
		return verdict("Pending", `'${wanted}' is not a rule this question can be decided by`);
	}

	const wantsTicked = wanted === "Checked";
	const state = ticked ? "ticked" : "not ticked";
	const requirement = wantsTicked ? "ticked" : "not ticked";
	const value = `${state}, and the rule requires it ${requirement}`;
	return verdict(Boolean(ticked) === wantsTicked ? "Compliant" : "Non-Compliant", value);
}

function evaluateSelect(rule, answer) {
	const value = text(answer, "answer_select");
	if (!value) return verdict("Pending", "not answered");

	const options = lines(get(rule, "select_options", ""));
	const nonCompliant = lines(get(rule, "non_compliant_options", ""));

	// Exact match after trim, case-SENSITIVE: two options differing only by case
	// is an authoring error the publish gate catches, and folding case here would
	// silently merge them instead.
	if (nonCompliant.indexOf(value) !== -1) {
		return verdict("Non-Compliant", `'${value}' is a non-compliant answer on this question`);
	}
	if (options.indexOf(value) !== -1) {
		return verdict("Compliant", `'${value}' is an accepted answer on this question`);
	}

	const permitted = options.slice();
	nonCompliant.forEach((option) => {
		if (permitted.indexOf(option) === -1) permitted.push(option);
	});
	const listed = permitted.length ? permitted.join(", ") : "none configured";
	return verdict(
		"Pending",
		`'${value}' is not one of this question's options (${listed}) — the template may have changed`
	);
}

function evaluateDate(rule, answer, today) {
	const raw = get(answer, "answer_date", null);
	if (raw === null || raw === undefined || raw === "") {
		return verdict("Pending", "not answered");
	}

	const value = asDate(raw);
	if (value === null) {
		return verdict("Pending", `'${raw}' is not a date this engine can read`);
	}

	const dateRule = text(rule, "date_rule") || DEFAULTS.date_rule;
	if (dateRule === "No Rule") {
		return verdict(
			"Pending",
			`${isoDate(value)} recorded; this question has no rule to decide it`
		);
	}

	const reference = asDate(today);
	if (reference === null) {
		// The Python raises `ValueError` here, because a missing `today` is a
		// programming error in the caller rather than user data. On a capture
		// screen the same mistake must still not throw, so it degrades to Pending
		// and says so — the one place this mirror deliberately differs from the
		// engine, and it differs only for an input the engine treats as a bug.
		return verdict("Pending", "this screen could not read today's date");
	}

	const days = dayDelta(value, reference);
	const threshold = integer(get(rule, "days_threshold", 0), 0);

	if (dateRule === "Must Be In Future") {
		if (days > 0) {
			return verdict("Compliant", `${isoDate(value)} is ${days} day(s) in the future`);
		}
		if (days === 0) {
			return verdict(
				"Non-Compliant",
				`${isoDate(value)} is today, and a future date is required`
			);
		}
		return verdict("Non-Compliant", `${isoDate(value)} was ${Math.abs(days)} day(s) ago`);
	}

	if (dateRule === "Expires Within Days") {
		if (threshold <= 0) {
			return verdict(
				"Pending",
				`${isoDate(value)} recorded; this question's Expires Within Days rule has no day threshold`
			);
		}
		if (days < 0) return verdict("Non-Compliant", `expired ${Math.abs(days)} day(s) ago`);
		if (days === 0) return verdict("Non-Compliant", "expires today");
		if (days <= threshold) {
			return verdict("Non-Compliant", `expires in ${days} day(s) (threshold ${threshold})`);
		}
		return verdict("Compliant", `expires in ${days} day(s) (threshold ${threshold})`);
	}

	if (dateRule === "Must Be Within Past Days") {
		if (threshold <= 0) {
			return verdict(
				"Pending",
				`${isoDate(value)} recorded; this question's Must Be Within Past Days rule has no day threshold`
			);
		}
		if (days > 0) return verdict("Non-Compliant", `${isoDate(value)} is in the future`);
		if (Math.abs(days) <= threshold) {
			return verdict(
				"Compliant",
				`${Math.abs(days)} day(s) ago (within the last ${threshold})`
			);
		}
		return verdict("Non-Compliant", `${Math.abs(days)} day(s) ago (older than ${threshold})`);
	}

	return verdict("Pending", `'${dateRule}' is not a date rule this engine can evaluate`);
}

function evaluateAttachment(rule, answer, answerType) {
	const noun = answerType === "Photo" ? "photo" : "document";
	const count = integer(get(answer, "attachment_count", 0), 0);
	if (count > 0) return verdict("Compliant", `${count} ${noun}(s) attached`);

	// Never Non-Compliant: scoring a missing photograph as a failure would let an
	// inspector fail a working extinguisher by forgetting a camera.
	if (flag(rule, "mandatory")) {
		return verdict("Pending", `a ${noun} is required and none is attached`);
	}
	return verdict("Pending", "not answered");
}

qhse.facility.rules = {
	DEFAULT_PRECISION,
	TEXT_TYPES,
	ATTACHMENT_TYPES,

	/** `{verdict, reason}` for one answer against one question row.
	 *
	 * `rule` is one question row from `entry_form`'s `questions`; `answer` is the
	 * live answer shape the capture screen holds in module state — the same keys
	 * `save_responses` accepts (`RESPONSE_FIELDS`). `today` is `YYYY-MM-DD`.
	 */
	evaluate(rule, answer, today) {
		const answerType = text(rule, "answer_type") || DEFAULTS.answer_type;

		// N/A is decided before the type switch, and the asymmetry is deliberate:
		// honouring a flag the template author switched off would silently let an
		// inspector skip a question the template says may not be skipped.
		if (flag(answer, "not_applicable")) {
			if (flag(rule, "allow_na")) {
				return verdict("Not Applicable", "marked not applicable by the inspector");
			}
			return verdict("Pending", "N/A is not permitted on this question");
		}

		if (TEXT_TYPES.indexOf(answerType) !== -1) return evaluateText(answer);
		if (answerType === "Numeric") return evaluateNumeric(rule, answer);
		if (answerType === "Checkbox") return evaluateCheckbox(rule, answer);
		if (answerType === "Single Select") return evaluateSelect(rule, answer);
		if (answerType === "Date") return evaluateDate(rule, answer, today);
		if (ATTACHMENT_TYPES.indexOf(answerType) !== -1) {
			return evaluateAttachment(rule, answer, answerType);
		}

		return verdict(
			"Pending",
			`'${answerType}' is not an answer type this engine can evaluate`
		);
	},

	/** The mirror of `facility_compliance.mandatory_outstanding`.
	 *
	 * `row` carries the answer AND the question attributes copied onto the
	 * response — `verdict`, `mandatory`, `answer_type`, `answer_text`,
	 * `text_acknowledged`. It answers *"is this what is standing between the
	 * entry and `Completed`?"*, which is the question the Save gate asks and the
	 * server's `set_entry_status` asks, so the two must be the same sentence.
	 *
	 * Like every other function in this file it is a **display**: the refusal
	 * that matters is the server's. The parity harness compares this against the
	 * Python over every fixture case, so a divergence fails a test rather than
	 * turning a disabled button into a refusal after the press — the one thing
	 * `PHASE-9.md` §8.5's contract forbids.
	 */
	mandatoryOutstanding(row) {
		if (GRADED_VERDICTS.indexOf(text(row, "verdict")) !== -1) return false;
		if (!flag(row, "mandatory")) return false;
		return !textAcknowledged(row);
	},

	/** Whether this question wants a photograph *right now*.
	 *
	 * `PHASE-9.md` §8.5: the prompt appears when `photo_required_when` is
	 * `On Non-Compliance` **and the rule just fired** — so it is a function of the
	 * live verdict, not of the question alone. `Always` prompts from the start;
	 * `Never` never does.
	 */
	photoWanted(rule, liveVerdict) {
		const when = text(rule, "photo_required_when");
		if (when === "Always") return true;
		if (when === "On Non-Compliance") return liveVerdict === "Non-Compliant";
		return false;
	},

	/** The answer field one answer type writes into. Schema, not opinion. */
	answerField(answerType) {
		if (TEXT_TYPES.indexOf(answerType) !== -1) return "answer_text";
		if (answerType === "Numeric") return "answer_number";
		if (answerType === "Checkbox") return "answer_check";
		if (answerType === "Single Select") return "answer_select";
		if (answerType === "Date") return "answer_date";
		if (ATTACHMENT_TYPES.indexOf(answerType) !== -1) return "attachment_count";
		return "answer_text";
	},

	// Exported for the mirror-parity harness, which needs the same coercions to
	// build its answers as the evaluators use to read them.
	_internals: {
		number, integer, lines, asDate, formatNumber, sharedPrecision, flag, text,
		textAcknowledged, TEXT_ACKNOWLEDGED_FIELD,
	},
};
